// src/screens/admin/ExportarArte.jsx
// Paso final compartido por los generadores de imagen del admin (orçamento de vuelo y
// paquete del Cotizador): formato (A4 / Story / Cuadrado), vista previa, tipo de archivo
// (PDF / PNG / JPEG) y los botones Bajar y Compartir.
import React from 'react';
import { FORMATOS } from '../../services/orcamentoRender';
import { labelStyle } from './CotizadorUI';
import { IDIOMAS } from '../../services/idiomaArte';

export const TIPOS = [
  { id: 'pdf', label: 'PDF', sub: 'Documento', mime: 'application/pdf', ext: 'pdf' },
  { id: 'png', label: 'PNG', sub: 'Imagen nítida', mime: 'image/png', ext: 'png' },
  { id: 'jpeg', label: 'JPEG', sub: 'Imagen liviana', mime: 'image/jpeg', ext: 'jpg' },
];
export const TIPO_POR_FORMATO = { a4: 'pdf', story: 'png', cuadrado: 'png' };

export const CSS_ARTE = `
.ml-chip{min-height:44px;padding:0 16px;border-radius:12px;border:1px solid var(--navy-border);background:var(--navy-card);color:var(--white);font-size:14px;font-weight:600;cursor:pointer;transition:background .18s,border-color .18s,transform .12s;white-space:nowrap;display:inline-flex;align-items:center;gap:8px}
.ml-chip:active{transform:scale(.97)}
.ml-chip[aria-pressed="true"]{background:var(--orange);border-color:var(--orange);color:#fff}
.ml-chip:focus-visible,.ml-btn:focus-visible,.ml-drop:focus-visible{outline:2px solid var(--orange);outline-offset:2px}
.ml-btn{min-height:48px;border-radius:14px;font-size:15px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;gap:10px;cursor:pointer;transition:transform .12s,opacity .18s;padding:0 18px}
.ml-btn:active{transform:scale(.98)}
.ml-btn:disabled{opacity:.45;cursor:not-allowed}
.ml-drop{border:1.5px dashed var(--navy-border);border-radius:16px;padding:20px 16px;display:flex;flex-direction:column;align-items:center;gap:8px;cursor:pointer;background:var(--navy-card);color:var(--muted);text-align:center;width:100%;transition:border-color .18s,background .18s}
.ml-drop:hover{border-color:var(--orange)}
.ml-row{display:flex;gap:8px;overflow-x:auto;padding:2px 2px 8px;scrollbar-width:thin}
@keyframes ml-spin{to{transform:rotate(360deg)}}
.ml-spin{width:22px;height:22px;border-radius:50%;border:3px solid var(--navy-border);border-top-color:var(--orange);animation:ml-spin .8s linear infinite}
@media (prefers-reduced-motion:reduce){.ml-spin{animation-duration:2.4s}.ml-chip,.ml-btn{transition:none}}
`;

// Comparte el archivo con el menú del teléfono (modo 'compartir') o lo descarga.
// Devuelve 'compartido', 'descargado' o 'cancelado' (el usuario cerró el menú de compartir).
export async function entregarArchivo({ blob, nombre, mime, modo }) {
  const file = new File([blob], nombre, { type: mime });
  if (modo === 'compartir' && navigator.canShare?.({ files: [file] })) {
    try { await navigator.share({ files: [file] }); return 'compartido'; } catch (e) { if (e?.name === 'AbortError') return 'cancelado'; }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = nombre; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return 'descargado';
}

// Interruptor de encendido/apagado con texto al lado.
export function Interruptor({ activo, onChange, children }) {
  return (
    <button type="button" role="switch" aria-checked={activo} onClick={() => onChange(!activo)} className="ml-chip"
      style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 10, whiteSpace: 'normal', textAlign: 'left', padding: '8px 14px' }}>
      <span aria-hidden="true" style={{ width: 36, height: 20, borderRadius: 10, background: activo ? 'var(--orange)' : 'var(--navy-border)', position: 'relative', flexShrink: 0 }}>
        <span style={{ position: 'absolute', top: 2, left: activo ? 18 : 2, width: 16, height: 16, borderRadius: 8, background: '#fff', transition: 'left .15s' }} />
      </span>
      {children}
    </button>
  );
}

const Svg = ({ children }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IcDescarga = () => <Svg><path d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19h14" /></Svg>;
const IcCompartir = () => <Svg><circle cx="6" cy="12" r="2.5" /><circle cx="17" cy="6" r="2.5" /><circle cx="17" cy="18" r="2.5" /><path d="M8.2 10.8 14.8 7.2M8.2 13.2l6.6 3.6" /></Svg>;

// props: formato/onFormato, tipo/onTipo, vista (dataURL) y cargando, textoVacio (si no hay vista),
// aviso (nodo bajo la vista previa), listo (se puede exportar), exportando ('' | 'bajar' | 'compartir').
export default function ExportarArte({
  formato, onFormato, tipo, onTipo, vista, cargando, textoVacio, aviso, listo, exportando, onExportar, idioma, onIdioma,
}) {
  return (
    <div>
      {onIdioma && (
        <>
          <label style={labelStyle}>Idioma de la imagen</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }} role="group" aria-label="Idioma de la imagen">
            {IDIOMAS.map((i) => (
              <button key={i.id} type="button" className="ml-chip" aria-pressed={idioma === i.id} onClick={() => onIdioma(i.id)} style={{ justifyContent: 'center' }}>{i.label}</button>
            ))}
          </div>
        </>
      )}
      <label style={labelStyle}>Formato</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }} role="group" aria-label="Formato de la arte">
        {Object.values(FORMATOS).map((fm) => {
          const on = formato === fm.id;
          const ph = 34; const pw = Math.round(ph * (fm.w / fm.h));
          return (
            <button key={fm.id} type="button" aria-pressed={on} onClick={() => onFormato(fm.id)} style={{
              minHeight: 96, borderRadius: 14, padding: '10px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer',
              background: on ? 'rgba(255,107,0,0.10)' : 'var(--navy-card)', border: `1.5px solid ${on ? 'var(--orange)' : 'var(--navy-border)'}`, color: 'var(--white)',
            }}>
              <span aria-hidden="true" style={{ width: pw, height: ph, borderRadius: 4, border: `2px solid ${on ? 'var(--orange)' : 'var(--muted)'}`, background: on ? 'rgba(255,107,0,0.18)' : 'transparent' }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: on ? 'var(--orange)' : 'var(--white)' }}>{fm.label}</span>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>{fm.detalle}</span>
            </button>
          );
        })}
      </div>

      <div style={{ position: 'relative', minHeight: 120, borderRadius: 14, overflow: 'hidden', background: 'var(--navy)', border: '1px solid var(--navy-border)', maxWidth: formato === 'story' ? 300 : '100%', margin: '0 auto' }}>
        {vista ? (
          <img src={vista} alt="Vista previa de la arte" style={{ width: '100%', display: 'block', opacity: cargando ? 0.55 : 1, transition: 'opacity .2s' }} />
        ) : (
          <p style={{ padding: 28, textAlign: 'center', fontSize: 13, color: 'var(--muted)', margin: 0 }}>{textoVacio}</p>
        )}
        {cargando && <span className="ml-spin" role="status" aria-label="Actualizando vista previa" style={{ position: 'absolute', top: 12, right: 12 }} />}
      </div>
      {aviso}

      <label style={{ ...labelStyle, marginTop: 16 }}>Tipo de archivo</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }} role="group" aria-label="Tipo de archivo">
        {TIPOS.map((t) => (
          <button key={t.id} type="button" className="ml-chip" aria-pressed={tipo === t.id} onClick={() => onTipo(t.id)} style={{ flexDirection: 'column', gap: 2, padding: '8px 6px', whiteSpace: 'normal', justifyContent: 'center' }}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>{t.label}</span>
            <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.85 }}>{t.sub}</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16 }}>
        <button type="button" className="ml-btn btn-primary" disabled={!listo || !!exportando} onClick={() => onExportar('bajar')} style={{ width: 'auto' }}>
          <IcDescarga />{exportando === 'bajar' ? 'Generando…' : 'Bajar'}
        </button>
        <button type="button" className="ml-btn" disabled={!listo || !!exportando} onClick={() => onExportar('compartir')} style={{ background: 'transparent', border: '1.5px solid var(--orange)', color: 'var(--orange)' }}>
          <IcCompartir />{exportando === 'compartir' ? 'Generando…' : 'Compartir'}
        </button>
      </div>
    </div>
  );
}
